import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import { Search, ChevronDown, ChevronRight, Eye, Download } from "lucide-react";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";

const MobileUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);

  useEffect(() => {
    fetchMobileUsers();
  }, []);

  const fetchMobileUsers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/mobile-users");
      setUsers(res.data?.users || []);
    } catch (err) {
      toast.error("Failed to fetch retail users");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  const downloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (cityFilter) params.append("city", cityFilter);

      const res = await apiClient.get(`/mobile-users/export/excel?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `retail_users.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Excel downloaded successfully");
    } catch (error) {
      console.error("Error downloading excel:", error);
      toast.error("Failed to download excel");
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const availableCities = useMemo(() => {
    const cities = new Set();
    users.forEach(u => {
      const defaultAddress = u.addresses?.find(a => a.isDefault) || u.addresses?.[0] || {};
      if (defaultAddress.city) {
        cities.add(defaultAddress.city);
      }
    });
    return Array.from(cities).sort();
  }, [users]);

  /* ================= FILTER ================= */
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const defaultAddress = u.addresses?.find(a => a.isDefault) || u.addresses?.[0] || {};
      const matchesSearch =
        !search ||
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.mobile?.includes(search);

      const matchesCity = !cityFilter || defaultAddress.city === cityFilter;

      return matchesSearch && matchesCity;
    });
  }, [users, search, cityFilter]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Retail Users</h1>
          <p className="text-sm text-slate-500 mt-1">
            View registered retail application users
          </p>
        </div>
      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 w-full px-4 items-center">
        <div className="relative flex-1 w-full">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Search by name, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-3 py-2 w-full h-10 text-sm bg-white border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition min-w-[120px]"
          >
            <option value="">All Cities</option>
            {availableCities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <button
            onClick={downloadExcel}
            disabled={isDownloadingExcel}
            className="h-10 px-4 bg-[#172554] text-white rounded-md hover:bg-[#1e2f6a] transition-all flex items-center justify-center gap-2 text-sm font-medium active:scale-95 disabled:opacity-50 shrink-0"
            title="Export Excel"
          >
            {isDownloadingExcel ? (
              <Loader inline={true} text="" />
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export Excel</span>
                <span className="sm:hidden">Export</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-lg bg-white flex-1 min-h-0 flex flex-col">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <ThemedTable className="text-left border-collapse w-full">
            <thead className="sticky top-0 z-20 bg-white shadow-sm">
              <tr className="bg-white text-gray-700">
                <th className="p-3 w-8"></th>
                <th className="p-3 w-16 text-center">#</th>
                <th className="p-3 w-3/12">Name</th>
                <th className="p-3 w-3/12">Phone</th>
                <th className="p-3 w-2/12 hidden md:table-cell">City</th>
                <th className="p-3 w-3/12 hidden xl:table-cell text-center">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center">
                    <Loader text="Loading retail users..." size={32} />
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user, idx) => {
                  const defaultAddress = user.addresses?.find(a => a.isDefault) || user.addresses?.[0] || {};
                  
                  return (
                    <React.Fragment key={user._id}>
                      <tr
                        onClick={() => toggleRow(user._id)}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors h-[60px] cursor-pointer"
                      >
                        <td className="p-3 text-slate-400">
                          {expandedRowId === user._id ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </td>
                        <td className="p-3 text-center font-medium">{idx + 1}</td>
                        <td className="p-3 font-medium capitalize">
                          <div className="flex items-center gap-2">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-[#172554] flex items-center justify-center font-bold text-sm uppercase">
                                {user.name ? user.name.slice(0, 2) : "RU"}
                              </div>
                            )}
                            <span>{user.name || "N/A"}</span>
                          </div>
                        </td>
                        <td className="p-3 font-medium text-slate-700 whitespace-nowrap">
                          {user.mobile || "-"}
                        </td>
                        <td className="p-3 hidden md:table-cell capitalize">
                          {defaultAddress.city || "-"}
                        </td>
                        <td className="p-3 hidden xl:table-cell text-center whitespace-nowrap text-slate-500">
                          {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : (user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-")}
                        </td>
                      </tr>
                      {expandedRowId === user._id && (
                        <tr className="bg-white border-b border-slate-200">
                          <td colSpan="6" className="px-6 py-4">
                            <div className="flex flex-col lg:flex-row gap-6">
                              {/* Left Column: Metadata */}
                              <div className="flex-1 space-y-4">
                                <div className="bg-white p-4 rounded-none border border-slate-200/60 text-xs">
                                  <p className="text-[10px] text-slate-400 uppercase font-bold mb-3">
                                    Retail User Profile & Stats
                                  </p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
                                    {/* Profile Column */}
                                    <div className="space-y-2.5">
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Company:</span>{" "}
                                        <span className="font-semibold text-right">-</span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Email:</span>{" "}
                                        <span className="font-semibold text-right">{user.email || "-"}</span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Phone:</span>{" "}
                                        <span className="font-semibold text-right">{user.mobile || "-"}</span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>City / State:</span>{" "}
                                        <span className="font-semibold capitalize text-right">
                                          {defaultAddress.city ? defaultAddress.city : "-"} {defaultAddress.state ? `, ${defaultAddress.state}` : ""}
                                        </span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Country / Zip:</span>{" "}
                                        <span className="font-semibold capitalize text-right">
                                          - {defaultAddress.pincode ? ` (${defaultAddress.pincode})` : ""}
                                        </span>
                                      </p>
                                      <div className="text-slate-600 flex justify-between pt-1">
                                        <span className="min-w-[70px]">Address:</span>{" "}
                                        <span className="font-semibold capitalize text-right whitespace-normal break-words">
                                          {defaultAddress.street || "-"}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Stats Column */}
                                    <div className="space-y-2.5">
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Total Orders:</span>{" "}
                                        <span className="font-semibold text-right">{user.orders?.length || 0}</span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Avg Spend:</span>{" "}
                                        <span className="font-semibold text-green-600 text-right">
                                          {user.orders?.length ? `₹ ${Math.round(user.orders.reduce((sum, o) => sum + (o.paymentDetails?.totalAmount || 0), 0) / user.orders.length)}` : "-"}
                                        </span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Last Order Amount:</span>{" "}
                                        <span className="font-semibold text-green-600 text-right">
                                          {user.orders?.[0]?.paymentDetails?.totalAmount != null ? `₹ ${Math.round(user.orders[0].paymentDetails.totalAmount)}` : "-"}
                                        </span>
                                      </p>
                                      {(() => {
                                        if (!user.orders?.length) return null;
                                        const productCounts = {};
                                        user.orders.forEach(o => {
                                          o.items?.forEach(i => {
                                            if (i.productName) {
                                              productCounts[i.productName] = (productCounts[i.productName] || 0) + (i.quantity || 1);
                                            }
                                          });
                                        });
                                        let topProduct = null;
                                        let maxQty = 0;
                                        for (const [name, qty] of Object.entries(productCounts)) {
                                          if (qty > maxQty) {
                                            topProduct = name;
                                            maxQty = qty;
                                          }
                                        }
                                        if (!topProduct) return null;
                                        return (
                                          <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                            <span className="min-w-[100px]">Most Purchased:</span>{" "}
                                            <span className="font-semibold text-blue-600 text-right whitespace-normal break-words" title={topProduct}>
                                              {topProduct} ({maxQty})
                                            </span>
                                          </p>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Right Column: Product Detail */}
                              <div className="flex-1 border-l border-slate-200/60 lg:pl-6">
                                <p className="text-[10px] text-slate-400 uppercase font-bold mb-3">
                                  Last Ordered Items
                                </p>
                                {user.orders && user.orders.length > 0 && user.orders[0]?.items?.length > 0 ? (
                                  <div className="space-y-2">
                                    {user.orders[0].items.map((item, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs bg-white p-2 rounded-none border border-slate-200/60 group hover:border-orange-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                                          <p className="font-semibold text-slate-800">{item.productName || "Product"}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-slate-400 italic py-2">
                                    No items found in last order
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                    No retail users found
                  </td>
                </tr>
              )}
            </tbody>
          </ThemedTable>
        </div>
      </div>
    </div>
  );
};

export default MobileUsers;
